import { FC, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { QESButton } from "@app/shared/qes-standards/Button";
import { NetworkProfileKeyPoints } from "@app/compliance-connect/issuer/network-profile/NetworkProfileKeyPoints";
import { NetworkProfileOverview } from "@app/compliance-connect/issuer/network-profile/NetworkProfileOverview";
import { NetworkProfileGapDetails } from "@app/compliance-connect/issuer/network-profile/NetworkProfileGapDetails";
import { NetworkProfileRegulatorCommunication } from "@app/compliance-connect/issuer/network-profile/NetworkProfileRegulatorCommunication";
import { NetworkProfileCommentLog } from "@app/compliance-connect/issuer/network-profile/NetworkProfileCommentLog";
import { NetworkProfileChartsSummary } from "@app/compliance-connect/issuer/network-profile/NetworkProfileChartsSummary";
import NetworkSubmissionService from "@app/compliance-connect/network-submission/networkSubmissionService";
import LoadingSpinner from "@app/shared/LoadingSpinner";

enum NetworkProfileTabViews { Overview, CommentLog }

export const NetworkProfilePage: FC = () => {
  const history = useHistory();
  const { submissionId } = useParams<{ submissionId?: string }>();
  const service = useMemo(() => new NetworkSubmissionService(), []);
  const [activeTab, setActiveTab] = useState<NetworkProfileTabViews>(NetworkProfileTabViews.Overview);

  const { data: comments, isError: isCommentsError, isLoading: isCommentsLoading } = useQuery({
    enabled: !!submissionId,
    queryFn: ({ signal }) => service.getSubmissionComments(Number(submissionId), signal),
    queryKey: ["ns-submission-comments", Number(submissionId)],
    staleTime: 5 * 60 * 1000,
  });
  const unreadCommentCount = comments ? comments.filter(comment => !comment.isRead).length : null;
  const hasNewComment = !!unreadCommentCount;

  const { data: metrics, isError, isLoading } = useQuery({
    enabled: !!submissionId,
    queryFn: ({ signal }) => service.getNetworkProfileMetrics(Number(submissionId), signal),
    queryKey: ["ns-network-profile-metrics", submissionId],
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (isError) {
      toast(<span>Failed to load network profile.</span>, { containerId: "z-index-9999", type: "error" });
      history.replace("/network-submission/view");
    }
  }, [isError, history]);

  return (
    <Page documentTitle="Network Profile">
      <Breadcrumbs template={BreadcrumbTemplateEnum.NetworkSubmissions} items={[{ link: "/network-submission", text: "Network Profile" }]} />
      <PageInner>
        <PageSection>
            <div className="compliance-connect-network-profile tab-content">
                <div className="mb-3">
                    <QESButton
                        qesTestId="network-profile__go-back"
                        qesType="outline"
                        onClick={() => history.push("/network-submission")}
                    >
                        Go Back
                    </QESButton>
                </div>
                {isLoading && <LoadingSpinner />}
                {!isLoading && !isError && (
                <>
                    <h1 className="compliance-connect-network-profile__title">{metrics?.networkName ?? ""}</h1>
                    <hr />
                    <div className="tabs-style-2 mb-3">
                        <ul className="nav nav-tabs" role="tablist">
                            <li>
                                <QESButton
                                    qesType="none"
                                    className={`nav-link text-dark fw-normal ${activeTab === NetworkProfileTabViews.Overview ? "active disabled" : ""}`}
                                    qesTestId="network-profile-nav__tab--overview"
                                    onClick={() => setActiveTab(NetworkProfileTabViews.Overview)}
                                    aria-disabled={activeTab === NetworkProfileTabViews.Overview}
                                    role="tab"
                                >
                                    Overview
                                </QESButton>
                            </li>
                            <li>
                                <QESButton
                                    qesType="none"
                                    className={`nav-link text-dark fw-normal ${activeTab === NetworkProfileTabViews.CommentLog ? "active disabled" : ""}`}
                                    qesTestId="network-profile-nav__tab--comment-log"
                                    onClick={() => setActiveTab(NetworkProfileTabViews.CommentLog)}
                                    aria-disabled={activeTab === NetworkProfileTabViews.CommentLog}
                                    role="tab"
                                >
                                    {hasNewComment && <span className="compliance-connect-network-profile__tab-dot" aria-hidden="true" />}
                                    Comment Log
                                </QESButton>
                            </li>
                        </ul>
                    </div>
                    {activeTab === NetworkProfileTabViews.Overview && (
                        <div className="compliance-connect-network-profile__grid">
                            <div className="compliance-connect-network-profile__grid-column">
                                <NetworkProfileKeyPoints metrics={metrics} />
                                <NetworkProfileOverview />
                                <NetworkProfileRegulatorCommunication
                                    onViewComments={() => setActiveTab(NetworkProfileTabViews.CommentLog)}
                                    unreadCommentCount={unreadCommentCount}
                                />
                            </div>
                            <div className="compliance-connect-network-profile__grid-column compliance-connect-network-profile__grid-column--span-2">
                                <div className="compliance-connect-network-profile__metrics-row">
                                    <NetworkProfileChartsSummary submissionId={Number(submissionId)} />
                                </div>
                                <NetworkProfileGapDetails submissionId={Number(submissionId)} />
                            </div>
                        </div>
                    )}
                    {activeTab === NetworkProfileTabViews.CommentLog && (
                        <div data-testid="network-profile__comment-log">
                            <NetworkProfileCommentLog
                                comments={comments ?? []}
                                isError={isCommentsError}
                                isLoading={isCommentsLoading}
                                submissionId={Number(submissionId)}
                            />
                        </div>
                    )}
                </>
                )}
            </div>
        </PageSection>
      </PageInner>
    </Page>
  );
};
